/**
 * 171 — where the money paid at receiving came from.
 *
 * create_purchase lowered the supplier's debt by `paid`, but no drawer ever saw
 * it (strict audit 2, H2: ₾5,661.50 in 60 days, 0 supplier payments). The three
 * receiving dialogs now ask: „სალაროდან" (a movement out of the open drawer,
 * which the Z counts), „ბანკით", „სხვა". The device remembers the last answer
 * per user and shop, so a shop that always pays the same way taps nothing.
 *
 * 1.60 (183) — „პირადი თანხით" ('owner'): the owner paid from his own pocket,
 * no drawer and no bank moved. Measured before it (2026-10-22): 484 purchases,
 * ₾37k in 90 days came through restock / a new product / a model, doors that
 * asked nothing, while the three receiving dialogs had 0 uses since 1.54. So
 * the question is asked on every door; the stock doors offer no drawer until
 * restock_product has a retry guard (a resent restock would take the cash out
 * of the Z twice).
 */
import type { Key } from './i18n'
import { OPENING_PURCHASE_NOTE } from './opening-stock'

export type PaidSource = 'drawer' | 'bank' | 'owner' | 'other'
const SOURCES: readonly PaidSource[] = ['drawer', 'bank', 'owner', 'other']

/** Which tiles a door shows: the receiving dialogs (create_purchase) or the
 *  doors that write stock themselves (restock, a new product, a model, Excel). */
export type SourceDoor = 'receive' | 'stock'
export const DOOR_SOURCES: Record<SourceDoor, readonly PaidSource[]> = {
  receive: ['drawer', 'bank', 'owner', 'other'],
  stock: ['owner', 'bank', 'other'],
}

/** The screen's words for a source (the tab row, the purchase detail). */
export const SOURCE_KEY: Record<PaidSource, Key> = {
  drawer: 'sup.src.drawer', bank: 'sup.src.bank', owner: 'sup.src.owner', other: 'sup.src.other',
}

/** The accountant's Excel is a document: Georgian whatever the device speaks. */
export const PAID_SOURCE_KA: Record<PaidSource, string> = {
  drawer: 'სალაროდან', bank: 'ბანკით', owner: 'პირადი თანხით', other: 'სხვა',
}

/** The rule the dialog shows before the server's (PAID_SOURCE_REQUIRED, SHIFT_REQUIRED). */
export function paidSourceError(paid: number, source: PaidSource | null, hasShift: boolean): Key | null {
  if (!(paid > 0)) return null
  if (!source) return 'sup.src.err'
  if (source === 'drawer' && !hasShift) return 'sup.src.errShift'
  return null
}

/**
 * What a 1.54 till adds to create_purchase: always its ref (the server tells it
 * from a 1.53 till by that, and replays it instead of booking twice), the source
 * only when money was paid, the shift only for the drawer.
 */
export function purchaseSourceArgs(a: { paid: number; source: PaidSource | null; shiftId: string | null; clientRef: string }) {
  const paid = a.paid > 0
  return {
    p_paid_source: paid ? a.source : null,
    p_shift_id: paid && a.source === 'drawer' ? a.shiftId : null,
    p_client_ref: a.clientRef,
  }
}

/** What a stock door adds to its RPC: the source only when money changed
 *  hands (the server writes it only then), and never the drawer. */
export function stockSourceArg(total: number, source: PaidSource | null): PaidSource | null {
  return total > 0 && source && DOOR_SOURCES.stock.includes(source) ? source : null
}

/**
 * 183: what one chunk of a stock purchase adds to its RPC (record_stock_purchase /
 * append_stock_purchase). Only a chunk that carries money names who paid: a first
 * chunk worth ₾0 opens the purchase with no source, and the first paid chunk names
 * it — the server keeps the first source written (Codex review 2026-10-22: an import
 * of more than 500 new products whose first 500 were free lost the source).
 */
export function chunkSourceArg(
  lines: readonly { quantity: number; unit_cost: number }[],
  source: PaidSource | null,
): { p_paid_source?: PaidSource } {
  const value = lines.reduce((s, l) => s + (l.quantity > 0 ? l.quantity * l.unit_cost : 0), 0)
  const src = stockSourceArg(value, source)
  return src ? { p_paid_source: src } : {}
}

/** The answer a dialog opens with: the remembered one, but never a „drawer" there
 *  is no open shift for, nor a tile this door does not show. */
export function startingSource(remembered: PaidSource | null, hasShift: boolean, door: SourceDoor = 'receive'): PaidSource | null {
  if (!remembered || !DOOR_SOURCES[door].includes(remembered)) return null
  return remembered === 'drawer' && !hasShift ? null : remembered
}

/** Per user and shop, like the feature cache: no identity, nothing kept. A stock
 *  door keeps its own answer: a receipt paid from the drawer must not re-ask the
 *  next restock (which offers no drawer). */
export function paidSourceKey(userId?: string | null, orgId?: string | null, door: SourceDoor = 'receive'): string | null {
  if (!userId || !orgId) return null
  return door === 'stock' ? `paid-source-stock:${userId}:${orgId}` : `paid-source:${userId}:${orgId}`
}

const r2 = (v: number) => Math.round(v * 100) / 100

/**
 * 1.60 — „მფლობელის ჯიბე": what the owner took out of the drawer in a period
 * (drawerOutflows' „owner" row) against what he paid into goods himself. The
 * difference is what really stayed with him; negative = he added from his own
 * pocket. Derived, nothing stored; never revenue, never profit.
 *
 * Purchases paid with no source (everything before 1.60) are counted apart and
 * never guessed into either side. The opening stock („საწყისი ნაშთი", 1.37) is
 * not money paid in the period.
 */
export function ownerPocket(
  took: number,
  purchases: { paid: number; paid_source?: PaidSource | null; note?: string | null }[],
) {
  let intoGoods = 0
  let unnamed = 0
  let unnamedCount = 0
  for (const p of purchases) {
    if (!(p.paid > 0)) continue
    if (p.paid_source === 'owner') intoGoods += p.paid
    else if (!p.paid_source && !(p.note ?? '').startsWith(OPENING_PURCHASE_NOTE)) { unnamed += p.paid; unnamedCount++ }
  }
  const t = r2(took)
  const g = r2(intoGoods)
  return { took: t, intoGoods: g, kept: r2(t - g) || 0, unnamed: r2(unnamed), unnamedCount }   // || 0: never „−₾0.00"
}

type Store = Pick<Storage, 'getItem' | 'setItem'>
const local = (): Store | null => (typeof localStorage === 'undefined' ? null : localStorage)

export function readPaidSource(key: string | null, store: Store | null = local()): PaidSource | null {
  if (!key || !store) return null
  try {
    const v = store.getItem(key)
    return SOURCES.includes(v as PaidSource) ? (v as PaidSource) : null
  } catch {
    return null
  }
}

export function savePaidSource(key: string | null, source: PaidSource, store: Store | null = local()): void {
  if (!key || !store) return
  try { store.setItem(key, source) } catch { /* a private window */ }
}
